/**
 * KPI Tracking for Feature Flags
 * Monitors performance metrics for each feature flag
 */

export interface KPIDataPoint {
  timestamp: number;
  value: number;
}

export interface KPIThreshold {
  metric: string;
  threshold: number;
  operator: 'lt' | 'gt' | 'lte' | 'gte';
  description: string;
}

class KPITracker {
  private readonly MAX_HISTORY = 100;
  private readonly STORAGE_PREFIX = 'kpi_';

  recordKPI(flag: string, metric: string, value: number): void {
    const key = `${this.STORAGE_PREFIX}${flag}_${metric}`;
    const history = this.getHistory(flag, metric);
    
    history.push({ timestamp: Date.now(), value });
    
    // Keep last 100 data points
    if (history.length > this.MAX_HISTORY) {
      history.shift();
    }
    
    try {
      localStorage.setItem(key, JSON.stringify(history));
    } catch (error) {
      console.warn('Failed to store KPI data:', error);
    }
  }

  getHistory(flag: string, metric: string): KPIDataPoint[] {
    const key = `${this.STORAGE_PREFIX}${flag}_${metric}`;
    try {
      const stored = localStorage.getItem(key);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  }

  getLatestValue(flag: string, metric: string): number | null {
    const history = this.getHistory(flag, metric);
    if (history.length === 0) return null;
    return history[history.length - 1].value;
  }

  getAverage(flag: string, metric: string, lastN: number = 10): number | null {
    const history = this.getHistory(flag, metric);
    if (history.length === 0) return null;
    
    const slice = history.slice(-lastN);
    const sum = slice.reduce((acc, point) => acc + point.value, 0);
    return sum / slice.length;
  }

  checkThreshold(flag: string, threshold: KPIThreshold): boolean {
    const value = this.getLatestValue(flag, threshold.metric);
    if (value === null) return true; // Pass if no data
    
    switch (threshold.operator) {
      case 'lt': return value < threshold.threshold;
      case 'gt': return value > threshold.threshold;
      case 'lte': return value <= threshold.threshold;
      case 'gte': return value >= threshold.threshold;
      default: return true;
    }
  }

  clearHistory(flag: string, metric?: string): void {
    if (metric) {
      const key = `${this.STORAGE_PREFIX}${flag}_${metric}`;
      localStorage.removeItem(key);
    } else {
      // Clear all metrics for this flag
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith(`${this.STORAGE_PREFIX}${flag}_`)) {
          localStorage.removeItem(key);
        }
      });
    }
  }
}

export const kpiTracker = new KPITracker();

// Auto-track page load performance
if (typeof window !== 'undefined') {
  window.addEventListener('load', () => {
    const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
    if (navigation) {
      const loadTime = navigation.loadEventEnd - navigation.fetchStart;
      kpiTracker.recordKPI('GLOBAL', 'page-load-time', loadTime);
    }
  }, { once: true });
}
